import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, apiError } from "../lib/api";
import { downloadReport } from "../lib/nativeDownload";
import { AppShell, PageHeader } from "../components/AppShell";
import { LegalFooter } from "../components/LegalFooter";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { User, Mail, Download, KeyRound, ShieldAlert, Trash2, LifeBuoy } from "lucide-react";

export default function PrivacyData() {
  const navigate = useNavigate();
  const { user, refreshUser, logout } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [savingName, setSavingName] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [emailPwd, setEmailPwd] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [curPwd, setCurPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [pwdBusy, setPwdBusy] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [delConfirm, setDelConfirm] = useState("");
  const [delPwd, setDelPwd] = useState("");
  const [deleting, setDeleting] = useState(false);

  const saveName = async () => {
    setSavingName(true);
    try { await api.put("/account/profile", { name }); await refreshUser(); toast.success("Name updated"); }
    catch (e) { toast.error(apiError(e)); }
    setSavingName(false);
  };

  const requestEmail = async () => {
    setEmailBusy(true);
    try {
      await api.post("/account/email/request", { new_email: newEmail.trim().toLowerCase(), password: emailPwd, origin_url: window.location.origin });
      toast.success("Check your new inbox to confirm the change");
      setNewEmail(""); setEmailPwd("");
    } catch (e) { toast.error(apiError(e)); }
    setEmailBusy(false);
  };

  const changePwd = async () => {
    setPwdBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: curPwd, new_password: newPwd });
      toast.success("Password changed"); setCurPwd(""); setNewPwd("");
    } catch (e) { toast.error(apiError(e)); }
    setPwdBusy(false);
  };

  const exportData = async () => {
    try {
      const { outcome } = await downloadReport({ path: "/account/export", filename: "molaroo-my-data.json", mimeType: "application/json" });
      if (outcome === "cancelled") return;
      toast.success(outcome === "shared" ? "Your data is ready to save or share" : "Your data export has downloaded");
    } catch { toast.error("Could not export your data"); }
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      await api.post("/account/delete", { confirm: delConfirm, password: delPwd });
      toast.success("Your account has been deleted");
      await logout();
      navigate("/login", { replace: true });
    } catch (e) { toast.error(apiError(e)); }
    setDeleting(false);
  };

  const Card = ({ icon: Icon, title, desc, children, testId }) => (
    <div className="df-card p-4" data-testid={testId}>
      <div className="flex items-center gap-3 mb-1">
        <span className="h-10 w-10 rounded-full bg-[#E6F4F1] text-[#0D786A] flex items-center justify-center"><Icon size={18} /></span>
        <div><div className="font-medium text-slate-900">{title}</div><div className="text-xs text-slate-500">{desc}</div></div>
      </div>
      <div className="mt-3 space-y-2.5">{children}</div>
    </div>
  );

  return (
    <AppShell>
      <PageHeader title="Privacy & Data" subtitle="Access, correct, export or delete your information" onBack={() => navigate("/more")} />
      <div className="px-5 space-y-2.5 df-fade-in pb-4">
        <Card icon={User} title="Your name" desc={`Signed in as ${user?.email || ""}`} testId="pd-name">
          <Input data-testid="pd-name-input" value={name} onChange={(e) => setName(e.target.value)} className="h-11 rounded-xl bg-white" placeholder="Your name" />
          <Button data-testid="pd-name-save" onClick={saveName} disabled={savingName} className="w-full h-10 rounded-xl bg-[#0D786A] hover:bg-[#0A6055]">{savingName ? "Saving…" : "Save name"}</Button>
        </Card>

        <Card icon={Mail} title="Change email" desc="We'll email a confirmation link to the new address" testId="pd-email">
          <Input data-testid="pd-email-input" type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} className="h-11 rounded-xl bg-white" placeholder="New email" />
          <Input data-testid="pd-email-pwd" type="password" value={emailPwd} onChange={(e) => setEmailPwd(e.target.value)} className="h-11 rounded-xl bg-white" placeholder="Current password" />
          <Button data-testid="pd-email-submit" onClick={requestEmail} disabled={emailBusy || !newEmail || !emailPwd} variant="outline" className="w-full h-10 rounded-xl">{emailBusy ? "Sending…" : "Send confirmation link"}</Button>
        </Card>

        <Card icon={KeyRound} title="Change password" desc="Update your password" testId="pd-password">
          <Input data-testid="pd-cur-pwd" type="password" value={curPwd} onChange={(e) => setCurPwd(e.target.value)} className="h-11 rounded-xl bg-white" placeholder="Current password" />
          <Input data-testid="pd-new-pwd" type="password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} className="h-11 rounded-xl bg-white" placeholder="New password (min 8 chars)" />
          <Button data-testid="pd-pwd-submit" onClick={changePwd} disabled={pwdBusy || !curPwd || !newPwd} variant="outline" className="w-full h-10 rounded-xl">{pwdBusy ? "Updating…" : "Change password"}</Button>
        </Card>

        <Card icon={Download} title="Export my data" desc="Download all your Molaroo records as JSON" testId="pd-export">
          <Button data-testid="pd-export-btn" onClick={exportData} variant="outline" className="w-full h-10 rounded-xl">Download my data</Button>
          <p className="text-[11px] text-slate-400">Separate from the Accountant Pack. Excludes passwords, MFA secrets and security tokens.</p>
        </Card>

        <Card icon={LifeBuoy} title="Privacy requests" desc="Access, correction or other privacy requests" testId="pd-contact">
          <a href="mailto:privacy@navscora.com" data-testid="pd-contact-link" className="inline-block text-[#0D786A] font-medium text-sm df-pressable">privacy@navscora.com</a>
        </Card>

        <div className="df-card p-4 border border-red-100" data-testid="pd-delete-card">
          <div className="flex items-center gap-3 mb-1">
            <span className="h-10 w-10 rounded-full bg-red-50 text-red-500 flex items-center justify-center"><ShieldAlert size={18} /></span>
            <div><div className="font-medium text-slate-900">Delete account</div><div className="text-xs text-slate-500">Permanently remove your account and data</div></div>
          </div>
          <p className="text-xs text-slate-500 leading-relaxed mt-2">This deletes your profile, practices, work, payments, expenses, reserves, uploaded files and settings, and cancels any active subscription. This is different from "Reset my data" and cannot normally be undone.</p>
          <Button data-testid="pd-delete-open" onClick={() => setDelOpen(true)} variant="outline" className="w-full h-11 rounded-xl border-red-200 text-red-600 hover:bg-red-50 mt-3"><Trash2 size={16} className="mr-1.5" /> Delete my account</Button>
        </div>

        <LegalFooter className="pt-6" />
      </div>

      <Dialog open={delOpen} onOpenChange={setDelOpen}>
        <DialogContent className="rounded-2xl max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600"><ShieldAlert size={18} /> Delete your account?</DialogTitle>
            <DialogDescription>
              This removes your Molaroo data from active systems and cancels any active subscription (Stripe keeps its own legally-required payment records). This cannot normally be undone. Type <strong>DELETE MY ACCOUNT</strong> and your password to confirm.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2.5">
            <Input data-testid="pd-delete-confirm" value={delConfirm} onChange={(e) => setDelConfirm(e.target.value)} placeholder="DELETE MY ACCOUNT" className="h-11 rounded-xl" />
            <Input data-testid="pd-delete-pwd" type="password" value={delPwd} onChange={(e) => setDelPwd(e.target.value)} placeholder="Your password" className="h-11 rounded-xl" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelOpen(false)} className="rounded-xl">Cancel</Button>
            <Button data-testid="pd-delete-confirm-btn" onClick={doDelete} disabled={deleting || delConfirm.trim().toUpperCase() !== "DELETE MY ACCOUNT" || !delPwd} className="rounded-xl bg-red-600 hover:bg-red-700">{deleting ? "Deleting…" : "Delete forever"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
